import { Skeleton } from "@/components/kit";
import { formatQtyWithUnit } from "@/modules/products/domain/unit-conversion";
import type { StockValuationVariantRowDTO } from "@/modules/reports/domain/types";
import { formatLak } from "@/shared/lib/currency";
import {
  ReportMobileCard,
  ReportProductLink,
  ReportStat,
} from "./ReportProductLink";

type StockValuationTableProps = {
  rows?: StockValuationVariantRowDTO[];
  isLoading?: boolean;
  embedded?: boolean;
};

function StockValuationMobileCard({
  row,
}: {
  row: StockValuationVariantRowDTO;
}) {
  return (
    <ReportMobileCard>
      <ReportProductLink
        productId={row.productId}
        productName={row.productName}
        variantName={row.variantName}
      />
      <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
        <ReportStat
          label="ຄົງເຫຼືອ"
          value={formatQtyWithUnit(row.onHandQty, row.stockUnitLabel)}
        />
        <ReportStat
          label="ລະຫວ່າງທາງ"
          value={formatQtyWithUnit(row.inTransitQty, row.stockUnitLabel)}
        />
        <ReportStat
          label="ມູນຄ່າຄົງເຫຼືອ"
          value={formatLak(row.onHandValueLak)}
        />
        <ReportStat
          label="ມູນຄ່າ incoming"
          value={formatLak(row.inTransitValueLak)}
        />
        <ReportStat
          label="ມູນຄ່າລວມ"
          value={formatLak(row.totalValueLak)}
          className="col-span-2"
          valueClassName="font-semibold text-secondary"
        />
      </dl>
    </ReportMobileCard>
  );
}

export function StockValuationTable({
  rows,
  isLoading,
  embedded,
}: StockValuationTableProps) {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {["a", "b", "c"].map((id) => (
          <Skeleton key={id} className="h-20 rounded-xl" />
        ))}
      </div>
    );
  }

  if (!rows?.length) {
    return (
      <div className="brand-card p-8 text-center text-muted-foreground text-sm">
        ບໍ່ມີຂໍ້ມູນມູນຄ່າຄລັງ
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-3 md:hidden">
        {rows.map((row) => (
          <StockValuationMobileCard key={row.variantId} row={row} />
        ))}
      </div>

      <div
        className={
          embedded
            ? "hidden overflow-x-auto md:block"
            : "hidden overflow-x-auto rounded-xl border border-secondary/20 md:block"
        }
      >
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-secondary/15 border-b bg-muted/30 text-left text-muted-foreground text-xs">
              <th className="px-4 py-3 font-medium">ສິນຄ້າ</th>
              <th className="px-4 py-3 font-medium text-right">ຄົງເຫຼືອ</th>
              <th className="px-4 py-3 font-medium text-right">ລະຫວ່າງທາງ</th>
              <th className="px-4 py-3 font-medium text-right">ມູນຄ່າຄົງເຫຼືອ</th>
              <th className="px-4 py-3 font-medium text-right">incoming</th>
              <th className="px-4 py-3 font-medium text-right">ລວມ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.variantId}
                className="border-secondary/10 border-b last:border-0"
              >
                <td className="px-4 py-3">
                  <ReportProductLink
                    productId={row.productId}
                    productName={row.productName}
                    variantName={row.variantName}
                  />
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {formatQtyWithUnit(row.onHandQty, row.stockUnitLabel)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {formatQtyWithUnit(row.inTransitQty, row.stockUnitLabel)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {formatLak(row.onHandValueLak)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {formatLak(row.inTransitValueLak)}
                </td>
                <td className="px-4 py-3 text-right font-medium text-secondary tabular-nums">
                  {formatLak(row.totalValueLak)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
